// ============================================================================
// @gs/ui — GsRowMenu
// Le menu « ... » d'une ligne ou d'une carte : toutes ses actions, libelle et icone
// ============================================================================
// Repris de la maquette UG (`apps/ug/src/fil/MenuActions.tsx`, branche feat-ug-un-seul-fil, commit
// 4eab95f93), jamais par fusion de cette branche (lot feat-ui-le-menu-de-ligne, decision de David du
// 2026-10-21 : « on fait le menu au plus vite »). Le patron pro (David, 2026-10-05) : au repos, le
// « ... » est le seul indice qu'une ligne a des actions ; il ouvre la liste entiere, les actions
// destructives en rouge, en bas, apres un separateur.
//
// Clavier (WAI-ARIA APG, patron « menu button ») : Entree ou Espace ouvre et donne le focus a la
// premiere action ; fleche bas ouvre sur la premiere, fleche haut sur la derniere. Dans le menu :
// fleches haut et bas (en boucle), Debut et Fin ; Echap et Tab ferment et rendent le focus au « ... ».
// Une action desactivee reste atteignable au clavier (aria-disabled, jamais `disabled`) pour que sa
// raison se lise ; elle ne se declenche pas.
//
// Position FIXE calculee contre la fenetre, par un PORTAIL sur document.body (RENDU de la tour du
// 2026-10-23, lot feat-ug-le-fil-remplace-la-console) : sous un ancetre qui porte un `zoom` ou un
// `transform` (le dessin de la compagnie dans UG), `fixed` se mesurait depuis cet ancetre — menu
// decale et grossi. Sur body, il echappe aussi au defilement horizontal et au `overflow-hidden` d'un
// tableau ; il se ferme quand la page defile ou change de taille (sa place ne suivrait plus).
// `react-dom` vient deja a @gs/ui par @radix-ui/react-tooltip (GsTooltip, Tooltip.Portal) ; sa
// declaration dans package.json est une echarde du lot. Les evenements React du menu remontent
// l'arbre React comme avant (un portail garde ses ancetres React).
//
// Aucune chaine en dur : le nom du « ... » et chaque libelle viennent de l'appelant (zero t() dans
// @gs/ui, I18N.skill.md).

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface GsRowMenuAction {
  /** Cle stable de l'action (sert de key React). */
  id: string;
  /** Libelle visible, deja traduit par l'appelant. */
  label: string;
  /** Icone Lucide optionnelle (ex: <Pencil size={15} />). */
  icon?: ReactNode;
  /** Action destructive : rouge, placee en bas apres un separateur. */
  destructive?: boolean;
  /** Action presente mais indisponible : visible, atteignable, jamais declenchee. */
  disabled?: boolean;
  /** Pourquoi l'action est indisponible, affiche sous son libelle (deja traduit). */
  disabledReason?: string;
  /** Appelee au choix, apres la fermeture du menu ; recoit le rectangle du « ... » pour ancrer une suite. */
  onSelect: (anchor: DOMRect) => void;
}

export interface GsRowMenuProps {
  /** Nom accessible du « ... » et du menu, deja traduit (« Actions : Inventaire »). Obligatoire. */
  label: string;
  /** Les actions de la ligne, dans l'ordre d'affichage (les destructives passent en bas). */
  actions: GsRowMenuAction[];
  /** Taille visuelle du « ... » ; la cible tactile fait 44 px sur un ecran tactile dans les deux cas. */
  size?: 'sm' | 'md';
  /** Extension du conteneur. */
  className?: string;
}

/** Marge entre le menu et le bord de la fenetre, et entre le menu et le « ... ». */
const MARGE = 8;
const ECART = 4;

const TRIGGER_SIZE: Record<NonNullable<GsRowMenuProps['size']>, string> = {
  sm: 'h-7 w-7 pointer-coarse:h-11 pointer-coarse:w-11',
  md: 'h-8 w-8 pointer-coarse:h-11 pointer-coarse:w-11',
};

const TRIGGER =
  'inline-flex shrink-0 touch-manipulation items-center justify-center rounded-lg transition-colors ' +
  'text-slate-500 hover:bg-slate-100 hover:text-slate-800 ' +
  'dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100 ' +
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500/60 ' +
  'aria-expanded:bg-slate-100 aria-expanded:text-slate-800 dark:aria-expanded:bg-slate-700 dark:aria-expanded:text-slate-100';

const ITEM =
  'flex min-h-9 w-full touch-manipulation items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm font-medium ' +
  'outline-hidden transition-colors duration-150 pointer-coarse:min-h-11';
const ITEM_NEUTRE =
  `${ITEM} text-slate-700 hover:bg-slate-100 focus:bg-slate-100 ` +
  'dark:text-slate-200 dark:hover:bg-slate-700 dark:focus:bg-slate-700';
const ITEM_ROUGE =
  `${ITEM} text-red-600 hover:bg-red-50 focus:bg-red-50 ` +
  'dark:text-red-400 dark:hover:bg-red-950/40 dark:focus:bg-red-950/40';
const ITEM_INDISPONIBLE =
  `${ITEM} cursor-not-allowed text-slate-400 focus:bg-slate-50 dark:text-slate-500 dark:focus:bg-slate-700/50`;

interface Place {
  left: number;
  top: number;
  /** Faux tant que le menu n'est pas mesure : il est rendu invisible pour etre mesure. */
  mesure: boolean;
}

export function GsRowMenu({ label, actions, size = 'md', className = '' }: GsRowMenuProps) {
  const [place, setPlace] = useState<Place | null>(null);
  /** Ou poser le focus a l'ouverture : la premiere action ou la derniere. */
  const [depart, setDepart] = useState<'premiere' | 'derniere'>('premiere');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const ouvert = place !== null;

  const neutres = actions.filter((a) => !a.destructive);
  const rouges = actions.filter((a) => a.destructive);
  const separe = neutres.length > 0 && rouges.length > 0;

  function ouvrir(ou: 'premiere' | 'derniere') {
    if (actions.length === 0) return;
    setDepart(ou);
    setPlace({ left: 0, top: 0, mesure: false });
  }

  function fermer(rendreLeFocus: boolean) {
    setPlace(null);
    if (rendreLeFocus) triggerRef.current?.focus();
  }

  // Premier rendu ouvert : invisible, mesure, puis place sous le « ... » (aligne a droite), au-dessus
  // s'il manque de la place en bas, toujours dans la fenetre.
  useLayoutEffect(() => {
    if (!place || place.mesure) return;
    const r = triggerRef.current?.getBoundingClientRect();
    const m = menuRef.current?.getBoundingClientRect();
    if (!r || !m) return;
    const left = Math.max(MARGE, Math.min(r.right - m.width, window.innerWidth - m.width - MARGE));
    const dessous = r.bottom + ECART;
    const top =
      dessous + m.height > window.innerHeight - MARGE ? Math.max(MARGE, r.top - ECART - m.height) : dessous;
    setPlace({ left, top, mesure: true });
  }, [place]);

  // Une fois place : le focus va a la premiere (ou derniere) action ; un clic dehors, un defilement ou
  // un changement de taille de la fenetre ferment.
  useEffect(() => {
    if (!place?.mesure) return;
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]');
    const cible = depart === 'derniere' ? items?.[items.length - 1] : items?.[0];
    cible?.focus({ preventScroll: true });
    const dehors = (e: PointerEvent | MouseEvent) => {
      const noeud = e.target as Node;
      if (!menuRef.current?.contains(noeud) && !triggerRef.current?.contains(noeud)) setPlace(null);
    };
    const bouge = (e: Event) => {
      if (e.type === 'scroll' && menuRef.current?.contains(e.target as Node)) return;
      setPlace(null);
    };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('touchstart', dehors as EventListener);
    document.addEventListener('scroll', bouge, true);
    window.addEventListener('resize', bouge);
    return () => {
      document.removeEventListener('mousedown', dehors);
      document.removeEventListener('touchstart', dehors as EventListener);
      document.removeEventListener('scroll', bouge, true);
      window.removeEventListener('resize', bouge);
    };
  }, [place?.mesure, depart]);

  function clavierDuBouton(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      ouvrir('premiere');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      ouvrir('derniere');
    }
  }

  function clavierDuMenu(e: KeyboardEvent<HTMLDivElement>) {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    const aller = (n: number) => {
      e.preventDefault();
      items[(n + items.length) % items.length]?.focus();
    };
    switch (e.key) {
      case 'ArrowDown':
        aller(i + 1);
        break;
      case 'ArrowUp':
        aller(i - 1);
        break;
      case 'Home':
        aller(0);
        break;
      case 'End':
        aller(items.length - 1);
        break;
      case 'Escape':
      case 'Tab':
        e.preventDefault();
        e.stopPropagation();
        fermer(true);
        break;
      default:
    }
  }

  function choisir(action: GsRowMenuAction) {
    if (action.disabled) return;
    const anchor = triggerRef.current?.getBoundingClientRect() ?? new DOMRect();
    fermer(true);
    action.onSelect(anchor);
  }

  const ligne = (action: GsRowMenuAction) => {
    // Le nom de l'action est son libelle seul ; la raison d'indisponibilite en est la description.
    const libelleId = `${menuId}-${action.id}-libelle`;
    const raisonId = action.disabled && action.disabledReason ? `${menuId}-${action.id}-raison` : undefined;
    const classes = action.disabled ? ITEM_INDISPONIBLE : action.destructive ? ITEM_ROUGE : ITEM_NEUTRE;
    const teinteIcone = action.disabled || action.destructive ? '' : 'text-slate-400 dark:text-slate-500';
    return (
      <button
        key={action.id}
        type="button"
        role="menuitem"
        tabIndex={-1}
        aria-disabled={action.disabled ? true : undefined}
        aria-labelledby={libelleId}
        aria-describedby={raisonId}
        data-destructive={action.destructive ? 'true' : undefined}
        onClick={() => choisir(action)}
        className={classes}
      >
        {action.icon ? (
          <span aria-hidden className={`flex shrink-0 ${teinteIcone}`}>
            {action.icon}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col">
          <span id={libelleId} className="truncate">
            {action.label}
          </span>
          {raisonId ? (
            <span id={raisonId} className="text-meta font-normal text-slate-500 dark:text-slate-400">
              {action.disabledReason}
            </span>
          ) : null}
        </span>
      </button>
    );
  };

  return (
    <div className={`inline-flex ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-controls={ouvert ? menuId : undefined}
        disabled={actions.length === 0}
        onClick={() => (ouvert ? fermer(false) : ouvrir('premiere'))}
        onKeyDown={clavierDuBouton}
        className={`${TRIGGER} ${TRIGGER_SIZE[size]} disabled:pointer-events-none disabled:opacity-40`}
      >
        <MoreHorizontal size={size === 'sm' ? 15 : 17} aria-hidden />
      </button>
      {place ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={clavierDuMenu}
          style={{ left: place.left, top: place.top, visibility: place.mesure ? 'visible' : 'hidden' }}
          className="fixed z-50 min-w-48 max-w-72 rounded-xl border border-slate-200 bg-white p-1 shadow-md dark:border-slate-700 dark:bg-slate-800"
        >
          {neutres.map(ligne)}
          {separe ? <div role="separator" className="-mx-1 my-1 h-px bg-slate-100 dark:bg-slate-700" /> : null}
          {rouges.map(ligne)}
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
